/**
 * Services List Route
 *
 * Displays searchable, filterable list of services with navigation.
 * Pagination, filtering, and sorting are handled server-side.
 */

import { useState, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useBulkOperations } from '../../hooks/useBulkOperations';
import { useServices, useAllTags, useTagCategories, useTeams } from '../../graphql/queries';
import { groupTagsByCategory } from '../../components/tags/TagPicker';
import {
  DataTable,
  StatusBadge,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  BulkActionToolbar,
  BulkDeleteConfirmationDialog,
  BulkUpdateDialog,
  type DataTableColumn,
  type BulkUpdateFieldConfig,
  type SortDirection,
} from '../../components/shared';
import type {
  Service,
  ServiceFilter,
  ServiceLifecycleStatus,
  ServiceTier,
} from '../../graphql/types';
import { bulkDeleteServices, bulkUpdateServices } from '../../lib/apiClient';
import { isScanLinked } from '../../lib/scanManagedFields';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/services/')({
  component: ServicesListPage,
  // All filters URL-sync (issue #1255), mirroring the Tags `categoryId`
  // pattern (frontend/src/routes/tags/index.tsx) — refresh/share-safe, and
  // lets Team Detail deep-link into `?teamId=<teamId>` as the default (but
  // still user-adjustable) filter.
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
    search: typeof search.search === 'string' ? search.search : '',
    lifecycleStatus:
      typeof search.lifecycleStatus === 'string'
        ? (search.lifecycleStatus as ServiceLifecycleStatus | '')
        : '',
    tier: typeof search.tier === 'string' ? (search.tier as ServiceTier | '') : '',
    tagId: typeof search.tagId === 'string' ? search.tagId : '',
    teamId: typeof search.teamId === 'string' ? search.teamId : '',
    // Deep-link from the Technology detail page's "used by" count+link
    // (issue #1815) — URL-only, no visible dropdown, mirroring how
    // /technologies treats supplierId/productFamilyId.
    technologyId: typeof search.technologyId === 'string' ? search.technologyId : '',
    // Scan-linkage filter (issue #2000). Optional so existing links that
    // navigate here without it stay valid.
    ...(search.scanLinked === 'linked' || search.scanLinked === 'unlinked'
      ? { scanLinked: search.scanLinked as ScanLinkedFilter }
      : {}),
  }),
});

// Export component for testing
export { ServicesListPage };

type ScanLinkedFilter = 'linked' | 'unlinked';

interface ServiceFilters {
  scanLinked?: ScanLinkedFilter;
  search: string;
  lifecycleStatus: ServiceLifecycleStatus | '';
  tier: ServiceTier | '';
  tagId: string;
  teamId: string;
  technologyId: string;
}

const SERVICE_BULK_FIELDS: BulkUpdateFieldConfig[] = [
  {
    key: 'lifecycleStatus',
    label: 'Lifecycle Status',
    type: 'select',
    options: [
      { value: 'prototype', label: 'Prototype' },
      { value: 'development', label: 'Development' },
      { value: 'production', label: 'Production' },
      { value: 'deprecated', label: 'Deprecated' },
      { value: 'retired', label: 'Retired' },
    ],
  },
  {
    key: 'tier',
    label: 'Tier',
    type: 'select',
    options: [
      { value: 'tier0', label: 'Tier 0 (Mission Critical)' },
      { value: 'tier1', label: 'Tier 1 (Business Critical)' },
      { value: 'tier2', label: 'Tier 2 (Important)' },
      { value: 'tier3', label: 'Tier 3 (Best Effort)' },
    ],
  },
];

const ForgeButton = 'forge-button' as React.ElementType;
const ForgeSelect = 'forge-select' as React.ElementType;
const ForgeOption = 'forge-option' as React.ElementType;
const ForgeOptionGroup = 'forge-option-group' as React.ElementType;

function ServicesListPage(): JSX.Element {
  const navigate = useNavigate();
  const { page, pageSize, search, lifecycleStatus, tier, tagId, teamId, technologyId, scanLinked } =
    Route.useSearch();

  // Filters are derived directly from the URL search params (issue #1255) —
  // the URL is the single source of truth, so a refresh/shared link restores
  // the exact same filtered view, and arriving with `?teamId=X` (e.g. from
  // Team Detail's "View Services" link) makes that the active filter without
  // any extra local state to keep in sync. `technologyId` (issue #1815)
  // follows the same pattern for the Technology detail page's "used by" link.
  const filters: ServiceFilters = {
    search,
    lifecycleStatus,
    tier,
    tagId,
    teamId,
    technologyId,
    ...(scanLinked ? { scanLinked } : {}),
  };

  const [sortColumn, setSortColumn] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // useAllTags (not the capped useTags(), issue #1484) — a tag catalog past
  // the resolver's 50-item default page would otherwise silently drop from
  // this filter dropdown, mirroring the fix already applied to TagPicker /
  // ServiceTagsAndCustomFields / BusinessEnvironmentFactorDetailPage (#1352).
  const { tags: allTags } = useAllTags();
  const { data: tagCategoriesData } = useTagCategories();
  const { data: teamsData } = useTeams();

  const tagCategories = tagCategoriesData?.tagCategories ?? [];
  const allTeams = teamsData?.teams?.edges?.map(({ node: team }) => team) ?? [];
  // Group tag filter options by Tag Group (Tag Category), matching the
  // Service detail page's tag picker presentation (issue #743). Falls back
  // to a flat list when categories haven't loaded yet / none exist.
  const tagOptionGroups =
    tagCategories.length > 0 ? groupTagsByCategory(allTags, tagCategories) : [];

  const serviceFilter: ServiceFilter | undefined = (() => {
    const f: ServiceFilter = {};
    if (filters.search) f.search = filters.search;
    if (filters.lifecycleStatus) f.lifecycleStatus = filters.lifecycleStatus;
    if (filters.tier) f.tier = filters.tier;
    if (filters.tagId) f.tagIds = [filters.tagId];
    if (filters.teamId) f.teamId = filters.teamId;
    if (filters.technologyId) f.technologyId = filters.technologyId;
    if (filters.scanLinked) f.scanLinked = filters.scanLinked === 'linked';
    return Object.keys(f).length > 0 ? f : undefined;
  })();

  const { data, loading, error } = useServices({
    filter: serviceFilter,
    page,
    pageSize,
    sortField: sortColumn,
    sortDirection,
  });

  const services = data?.services?.edges?.map((edge) => edge.node) ?? [];
  const totalCount = data?.services?.totalCount ?? 0;

  const bulk = useBulkOperations<Service, Parameters<typeof bulkUpdateServices>[1]>({
    rows: services,
    getRowId: (service) => service.entityId,
    getRowName: (service) => service.name,
    bulkDelete: (ids) => bulkDeleteServices(ids),
    bulkUpdate: (ids, updates) => bulkUpdateServices(ids, updates),
    refetchQueries: ['ListServices'],
    exportFileName: 'services-export',
  });

  const columns: DataTableColumn<Service>[] = [
    {
      key: 'name',
      label: 'Service Name',
      sortable: true,
      width: '25%',
      /* c8 ignore next 8 */
      render: (service) => (
        <div>
          <div className="entity-list-name-cell">{service.name}</div>
          {service.shortName && (
            <div className="entity-list-description-cell">{service.shortName}</div>
          )}
        </div>
      ),
    },
    {
      key: 'serviceType',
      label: 'Type',
      sortable: true,
      width: '15%',
    },
    {
      key: 'lifecycleStatus',
      label: 'Status',
      sortable: true,
      width: '15%',
      /* c8 ignore next 3 */
      render: (service) => (
        <StatusBadge variant="lifecycle" value={service.lifecycleStatus || 'development'} />
      ),
    },
    {
      key: 'tier',
      label: 'Tier',
      sortable: true,
      width: '10%',
      /* c8 ignore next */
      render: (service) => <StatusBadge variant="tier" value={service.tier} />,
    },
    {
      key: 'description',
      label: 'Description',
      width: '18%',
      /* c8 ignore next 10 */
      render: (service) => (
        <div className="entity-list-truncate-cell--fixed">{service.description || '—'}</div>
      ),
    },
    {
      key: 'scannerAppId',
      label: 'Scan linked',
      width: '8%',
      /* c8 ignore next 5 */
      render: (service) => (
        <span data-testid={`scan-linked-${service.entityId}`}>
          {isScanLinked(service) ? 'Linked' : 'Not linked'}
        </span>
      ),
    },
    {
      key: 'technologyCount',
      label: 'Technologies',
      sortable: true,
      width: '10%',
      /* c8 ignore next 5 */
      render: (service) => (
        <span
          data-testid={`tech-count-${service.entityId}`}
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {service.technologyCount ?? 0}
        </span>
      ),
    },
  ];

  const handleRowClick = (service: Service): void => {
    navigate({ to: `/services/${service.entityId}` });
  };

  const handleFilterChange = useCallback(
    (newFilters: Partial<ServiceFilters>): void => {
      void navigate({ to: '/services', search: { ...filters, ...newFilters, page: 1, pageSize } });
    },
    [navigate, pageSize, filters]
  );

  const handleClearFilters = (): void => {
    void navigate({
      to: '/services',
      search: {
        search: '',
        lifecycleStatus: '',
        tier: '',
        tagId: '',
        teamId: '',
        technologyId: '',
        page: 1,
        pageSize,
      },
    });
  };

  const handleSort = (column: string, direction: SortDirection): void => {
    setSortColumn(column);
    setSortDirection(direction);
    void navigate({ to: '/services', search: { ...filters, page: 1, pageSize } });
  };

  /* c8 ignore next 3 */
  const handlePageChange = (newPage: number): void => {
    void navigate({ to: '/services', search: { ...filters, page: newPage, pageSize } });
  };

  /* c8 ignore next 3 */
  const handlePageSizeChange = (newPageSize: number): void => {
    void navigate({ to: '/services', search: { ...filters, page: 1, pageSize: newPageSize } });
  };

  if (error) {
    return (
      <div className="services-list-page">
        <h1 className="forge-typography--headline4">Services</h1>
        <div className="entity-list-error-state">
          <EmptyState
            icon="error"
            title="Failed to load services"
            description={error.message}
            actionLabel="Retry"
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="services-list-page">
      <div className="services-header">
        <div>
          <h1 className="forge-typography--headline4">Services</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {loading ? 'Loading...' : `${totalCount} services`}
          </p>
        </div>
        <PermissionGate permission="service:create">
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => navigate({ to: '/services/new' })}
          >
            Create Service
          </ForgeButton>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search services..."
            aria-label="Search services"
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </div>

        <select
          className="filter-select"
          aria-label="Filter by lifecycle status"
          value={filters.lifecycleStatus}
          onChange={(e) =>
            handleFilterChange({ lifecycleStatus: e.target.value as ServiceLifecycleStatus | '' })
          }
        >
          <option value="">All Statuses</option>
          <option value="prototype">Prototype</option>
          <option value="development">Development</option>
          <option value="production">Production</option>
          <option value="deprecated">Deprecated</option>
          <option value="retired">Retired</option>
          <option value="unassessed">Not yet assessed</option>
        </select>

        <select
          className="filter-select"
          aria-label="Filter by tier"
          value={filters.tier}
          onChange={(e) => handleFilterChange({ tier: e.target.value as ServiceTier | '' })}
        >
          <option value="">All Tiers</option>
          <option value="tier0">Tier 0</option>
          <option value="tier1">Tier 1</option>
          <option value="tier2">Tier 2</option>
          <option value="tier3">Tier 3</option>
          <option value="unassigned">Not yet assigned</option>
        </select>

        <select
          className="filter-select"
          aria-label="Filter by scan linkage"
          value={filters.scanLinked ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            handleFilterChange({
              scanLinked: value === 'linked' || value === 'unlinked' ? value : undefined,
            });
          }}
        >
          <option value="">All (scan linkage)</option>
          <option value="linked">Scan linked</option>
          <option value="unlinked">Not scan linked</option>
        </select>

        <ForgeSelect
          name="tagId"
          label="Filter by tag"
          value={filters.tagId}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            handleFilterChange({ tagId: e.target.value })
          }
        >
          <ForgeOption value="">All Tags</ForgeOption>
          {tagOptionGroups.length > 0
            ? tagOptionGroups.map((group) => (
                <ForgeOptionGroup key={group.key} label={group.label}>
                  {group.tags.map((tag) => (
                    <ForgeOption key={tag.entityId} value={tag.entityId}>
                      {tag.name}
                    </ForgeOption>
                  ))}
                </ForgeOptionGroup>
              ))
            : allTags.map((tag) => (
                <ForgeOption key={tag.entityId} value={tag.entityId}>
                  {tag.name}
                </ForgeOption>
              ))}
        </ForgeSelect>

        <ForgeSelect
          name="teamId"
          label="Filter by team"
          value={filters.teamId}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            handleFilterChange({ teamId: e.target.value })
          }
        >
          <ForgeOption value="">All Teams</ForgeOption>
          {allTeams.map((team) => (
            <ForgeOption key={team.entityId} value={team.entityId}>
              {team.name}
            </ForgeOption>
          ))}
        </ForgeSelect>

        {(filters.search ||
          filters.lifecycleStatus ||
          filters.tier ||
          filters.tagId ||
          filters.teamId ||
          filters.technologyId ||
          filters.scanLinked) && (
          <ForgeButton type="button" variant="outlined" onClick={handleClearFilters}>
            Clear Filters
          </ForgeButton>
        )}
      </div>

      {/* Bulk Action Toolbar */}
      {bulk.selectedIds.size > 0 && (
        <BulkActionToolbar
          selectedCount={bulk.selectedIds.size}
          onClearSelection={bulk.clearSelection}
          onBulkDelete={() => bulk.setBulkDeleteOpen(true)}
          onBulkUpdate={() => bulk.setBulkUpdateOpen(true)}
          onBulkExport={bulk.handleBulkExport}
        />
      )}

      {/* Data Table */}
      <div className="services-table-container">
        {loading ? (
          <TableSkeleton rows={10} columns={6} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={services}
              getRowKey={(service) => service.entityId}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
              selectable
              selectedIds={bulk.selectedIds}
              onSelectionChange={bulk.setSelectedIds}
              emptyState={{
                icon: 'inbox',
                title: 'No services found',
                description:
                  filters.search ||
                  filters.lifecycleStatus ||
                  filters.tier ||
                  filters.tagId ||
                  filters.teamId ||
                  filters.technologyId ||
                  filters.scanLinked
                    ? 'Try adjusting your filters'
                    : 'Create your first service to get started',
                actionLabel: 'Create Service',
                onAction: () => navigate({ to: '/services/new' }),
              }}
            />
            <Pagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={page}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </div>

      {/* Bulk Delete Dialog */}
      <BulkDeleteConfirmationDialog
        open={bulk.bulkDeleteOpen}
        itemNames={bulk.selectedNames}
        onConfirm={() => void bulk.handleBulkDelete()}
        onCancel={() => bulk.setBulkDeleteOpen(false)}
        isDeleting={bulk.isBulkDeleting}
        result={bulk.bulkDeleteResult}
        onDone={bulk.handleBulkDeleteDone}
      />

      {/* Bulk Update Dialog */}
      <BulkUpdateDialog
        open={bulk.bulkUpdateOpen}
        selectedCount={bulk.selectedIds.size}
        entityLabel={{ singular: 'service', plural: 'services' }}
        fields={SERVICE_BULK_FIELDS}
        idPrefix="bulk-svc"
        selectClassName="filter-select"
        onConfirm={(updates) =>
          void bulk.handleBulkUpdate(updates as Parameters<typeof bulkUpdateServices>[1])
        }
        onCancel={() => bulk.setBulkUpdateOpen(false)}
        isUpdating={bulk.isBulkUpdating}
      />
    </div>
  );
}
